import { Component } from '@theme/component';

/**
 * Anchor Navigation Component
 * Manages scroll spy functionality for anchor navigation
 * Uses slideshow-component for dragging functionality
 */
export class AnchorNavigationComponent extends Component {
  constructor() {
    super();
    /** @type {HTMLElement[]} */
    this.links = [];
    /** @type {HTMLElement[]} */
    this.anchors = [];
    this.activeLinkIndex = 0;
    /** @type {IntersectionObserver | null} */
    this.intersectionObserver = null;
    /** @type {number | null} */
    this.scrollTimeout = null;
    /** @type {HTMLElement | null} */
    this.slideshow = null;
    /** @type {Array<(e: Event) => void>} */
    this.clickHandlers = [];
    /** @type {boolean} */
    this.isScrollingToAnchor = false;
    /** @type {number | null} */
    this.scrollToTimeout = null;
    /** @type {(() => void) | null} */
    this.scrollEndHandler = null;
  }

  connectedCallback() {
    super.connectedCallback();
    console.log('[AnchorNavigation] Component connected');
    
    // Update sticky position, width, and border-radius classes on section wrapper
    this.updateStickyPosition();
    this.updateSectionWidth();
    this.updateBorderRadius();
    
    // Listen for window resize to update responsive classes
    this.handleResize = () => {
      this.updateStickyPosition();
      this.updateSectionWidth();
      this.updateBorderRadius();
    };
    window.addEventListener('resize', this.handleResize);
    
    // Find the slideshow component
    this.slideshow = this.querySelector('slideshow-component');
    if (!this.slideshow) {
      console.warn('[AnchorNavigation] Slideshow component not found');
      return;
    }
    console.log('[AnchorNavigation] Slideshow found');

    // Find links within slideshow slides
    const slides = this.slideshow.querySelectorAll('slideshow-slide');
    console.log('[AnchorNavigation] Found slides:', slides.length);
    
    this.links = Array.from(slides)
      .map(slide => slide.querySelector('.anchor-navigation__link'))
      .filter(/** @returns {link is HTMLElement} */ (link) => link !== null);

    console.log('[AnchorNavigation] Found links:', this.links.length, this.links.map(l => l.textContent?.trim()));

    this.anchors = this.links.map(link => {
      const targetId = link.getAttribute('data-anchor-target');
      if (!targetId) return null;
      return document.getElementById(targetId);
    }).filter(/** @returns {anchor is HTMLElement} */ (anchor) => anchor !== null);

    console.log('[AnchorNavigation] Found anchors:', this.anchors.length, this.anchors.map(a => a.id));

    if (this.links.length === 0 || this.anchors.length === 0) {
      console.warn('[AnchorNavigation] No links or anchors found, aborting');
      return;
    }

    this.initClickHandlers();
    this.initScrollSpy();
    
    // Wait for DOM to be fully laid out before checking initial scroll position
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        console.log('[AnchorNavigation] Initial update, scrollY:', window.scrollY);
        // Reset activeLinkIndex to force update
        this.activeLinkIndex = -1;
        this.updateActiveLink();
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    if (this.scrollToTimeout) {
      clearTimeout(this.scrollToTimeout);
    }
    if (this.scrollEndHandler) {
      window.removeEventListener('scroll', this.scrollEndHandler);
      this.scrollEndHandler = null;
    }
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
      this.handleResize = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.widthResizeObserver) {
      this.widthResizeObserver.disconnect();
      this.widthResizeObserver = null;
    }
    this.removeClickHandlers();
  }

  /**
   * Initialize click handlers for anchor links
   */
  initClickHandlers() {
    this.clickHandlers = [];
    this.links.forEach((link, index) => {
      /**
       * @param {Event} e
       */
      const handler = (e) => {
        console.log(`[AnchorNavigation] Link ${index} clicked: "${link.textContent?.trim()}"`);
        
        // Prevent default anchor link behavior
        e.preventDefault();
        
        // Find the target anchor
        const targetId = link.getAttribute('data-anchor-target');
        const targetAnchor = targetId ? document.getElementById(targetId) : null;
        
        if (!targetAnchor) {
          console.warn(`[AnchorNavigation] Target anchor not found: ${targetId}`);
          return;
        }
        
        // Disable scroll spy while scrolling to anchor
        this.isScrollingToAnchor = true;
        this.setActiveLink(index);
        
        // Get header group height from CSS variable
        const headerGroupHeightStr = getComputedStyle(document.body).getPropertyValue('--header-group-height').trim();
        const headerGroupHeight = headerGroupHeightStr ? parseInt(headerGroupHeightStr, 10) : 0;
        
        // Get anchors section height directly from the section wrapper element
        const sectionWrapper = this.closest('.shopify-section.anchors-section');
        let anchorsSectionHeight = 0;
        if (sectionWrapper instanceof HTMLElement) {
          anchorsSectionHeight = sectionWrapper.getBoundingClientRect().height;
        }
        
        // Total offset is header height + anchors section height
        const totalOffset = headerGroupHeight + anchorsSectionHeight;
        
        // Calculate target scroll position with total offset
        const targetRect = targetAnchor.getBoundingClientRect();
        const targetScrollY = window.scrollY + targetRect.top - totalOffset;
        
        console.log(`[AnchorNavigation] Scrolling to anchor "${targetId}" at position ${targetScrollY} (header: ${headerGroupHeight}px, anchors section: ${anchorsSectionHeight}px, total: ${totalOffset}px)`);
        
        // Scroll to the anchor with smooth behavior
        window.scrollTo({
          top: targetScrollY,
          behavior: 'smooth'
        });
        
        // Set up scroll end detection
        this.detectScrollEnd();
      };
      this.clickHandlers.push(handler);
      link.addEventListener('click', handler);
    });
  }
  
  /**
   * Detect when scrolling to anchor has ended
   */
  detectScrollEnd() {
    // Clear any existing timeout and handler
    if (this.scrollToTimeout) {
      clearTimeout(this.scrollToTimeout);
    }
    if (this.scrollEndHandler) {
      window.removeEventListener('scroll', this.scrollEndHandler);
    }
    
    let lastScrollY = window.scrollY;
    let scrollEndCheckCount = 0;
    
    // Handler to detect when scroll has stopped
    this.scrollEndHandler = () => {
      const currentScrollY = window.scrollY;
      
      // If scroll position hasn't changed, increment counter
      if (Math.abs(currentScrollY - lastScrollY) < 1) {
        scrollEndCheckCount++;
        
        // After 3 consecutive checks with no movement, consider scroll ended
        if (scrollEndCheckCount >= 3) {
          console.log('[AnchorNavigation] Scroll to anchor completed');
          this.isScrollingToAnchor = false;
          
          // Clean up
          if (this.scrollEndHandler) {
            window.removeEventListener('scroll', this.scrollEndHandler);
            this.scrollEndHandler = null;
          }
          if (this.scrollToTimeout) {
            clearTimeout(this.scrollToTimeout);
            this.scrollToTimeout = null;
          }
          
          // Update active link based on final position
          requestAnimationFrame(() => {
            this.updateActiveLink();
          });
        }
      } else {
        // Scroll position changed, reset counter
        scrollEndCheckCount = 0;
        lastScrollY = currentScrollY;
      }
    };
    
    // Start listening for scroll events
    window.addEventListener('scroll', this.scrollEndHandler);
    
    // Fallback timeout to re-enable scroll spy after 2 seconds
    this.scrollToTimeout = setTimeout(() => {
      console.log('[AnchorNavigation] Scroll timeout fallback - re-enabling scroll spy');
      this.isScrollingToAnchor = false;
      
      if (this.scrollEndHandler) {
        window.removeEventListener('scroll', this.scrollEndHandler);
        this.scrollEndHandler = null;
      }
      
      this.updateActiveLink();
    }, 2000);
  }

  /**
   * Remove click handlers
   */
  removeClickHandlers() {
    this.links.forEach((link, index) => {
      if (this.clickHandlers[index]) {
        link.removeEventListener('click', this.clickHandlers[index]);
      }
    });
    this.clickHandlers = [];
  }

  /**
   * Initialize scroll spy using Intersection Observer
   */
  initScrollSpy() {
    const options = {
      root: null,
      rootMargin: '-15% 0px -70% 0px', // Trigger when anchor enters upper portion of viewport
      threshold: [0, 0.1, 0.5, 1]
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      // Don't update if we're programmatically scrolling to an anchor
      if (this.isScrollingToAnchor) {
        console.log('[AnchorNavigation] IntersectionObserver ignored (scrolling to anchor)');
        return;
      }
      console.log('[AnchorNavigation] IntersectionObserver triggered', entries.length, 'entries');
      // Use scroll position method instead for more accurate detection
      // This ensures we highlight the current section until next anchor is reached
      this.updateActiveLink();
    }, options);

    // Observe all anchors
    this.anchors.forEach((anchor) => {
      if (this.intersectionObserver) {
        this.intersectionObserver.observe(anchor);
      }
    });

    // Listen to scroll events for active link updates
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  /**
   * Handle scroll event as fallback
   */
  handleScroll = () => {
    // Don't update if we're programmatically scrolling to an anchor
    if (this.isScrollingToAnchor) {
      console.log('[AnchorNavigation] Scroll event ignored (scrolling to anchor)');
      return;
    }

    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      console.log('[AnchorNavigation] Scroll event - updating active link, scrollY:', window.scrollY);
      this.updateActiveLink();
    }, 100);
  };

  /**
   * Update active link based on scroll position
   * Highlights the link for the current section until the next anchor is reached
   */
  updateActiveLink() {
    const scrollY = window.scrollY;
    console.log('[AnchorNavigation] updateActiveLink - scrollY:', scrollY);

    let activeIndex = 0;
    
    // Find the last anchor that we've scrolled past
    // We highlight an anchor when we've passed it but haven't reached the next one
    for (let i = 0; i < this.anchors.length; i++) {
      const anchor = this.anchors[i];
      if (!anchor) continue;
      
      const anchorRect = anchor.getBoundingClientRect();
      const anchorTop = anchorRect.top + scrollY;
      
      console.log(`[AnchorNavigation] Anchor ${i} (${anchor.id}): top=${anchorTop.toFixed(2)}, scrollY=${scrollY.toFixed(2)}, passed=${scrollY >= anchorTop}`);
      
      // Check if we've scrolled past this anchor's top
      if (scrollY >= anchorTop) {
        // This anchor has been passed, so it could be active
        activeIndex = i;
        
        // Check if there's a next anchor
        if (i < this.anchors.length - 1) {
          const nextAnchor = this.anchors[i + 1];
          if (nextAnchor) {
            const nextAnchorTop = nextAnchor.getBoundingClientRect().top + scrollY;
            console.log(`[AnchorNavigation] Next anchor ${i + 1} (${nextAnchor.id}): top=${nextAnchorTop.toFixed(2)}`);
            
            // If we've also passed the next anchor, continue to check
            // Otherwise, this anchor is active (we've passed it but not the next one)
            if (scrollY < nextAnchorTop) {
              console.log(`[AnchorNavigation] Selected index ${i} (passed anchor ${i}, haven't reached ${i + 1})`);
              break;
            }
            // If we've reached the next anchor, continue loop to check if we've passed it
          }
        } else {
          // This is the last anchor, so it's active if we've passed it
          console.log(`[AnchorNavigation] Selected index ${i} (last anchor, passed)`);
          break;
        }
      } else {
        // We haven't reached this anchor yet
        // If this is the first anchor and we're before it, select it
        if (i === 0) {
          activeIndex = 0;
          console.log(`[AnchorNavigation] Selected index 0 (before first anchor)`);
        } else {
          // We're before this anchor, so the previous one should be active
          activeIndex = i - 1;
          console.log(`[AnchorNavigation] Selected index ${activeIndex} (before anchor ${i})`);
        }
        break;
      }
    }

    console.log('[AnchorNavigation] Final activeIndex:', activeIndex);
    this.setActiveLink(activeIndex);
  }

  /**
   * Set the active link
   * @param {number} index
   */
  setActiveLink(index) {
    console.log(`[AnchorNavigation] setActiveLink called with index ${index}, current activeIndex: ${this.activeLinkIndex}`);
    
    if (index === this.activeLinkIndex) {
      console.log('[AnchorNavigation] Index unchanged, skipping');
      return;
    }
    
    if (index < 0 || index >= this.links.length) {
      console.warn(`[AnchorNavigation] Invalid index ${index}, links length: ${this.links.length}`);
      return;
    }

    const link = this.links[index];
    if (!link) {
      console.warn(`[AnchorNavigation] Link not found at index ${index}`);
      return;
    }

    console.log(`[AnchorNavigation] Setting active link to index ${index}: "${link.textContent?.trim()}"`);

    // Remove active class from all links
    this.links.forEach((link) => link.classList.remove('active'));

    // Add active class to current link
    link.classList.add('active');
    this.activeLinkIndex = index;

    // Scroll the active link into view in the navigation container
    this.scrollLinkIntoView(link);
  }

  /**
   * Scroll the active link into view within the navigation container
   * @param {HTMLElement} link
   */
  scrollLinkIntoView(link) {
    if (!this.slideshow) return;
    
    const scroller = this.slideshow.querySelector('slideshow-slides');
    if (!scroller) return;

    const linkRect = link.getBoundingClientRect();
    const containerRect = scroller.getBoundingClientRect();

    const linkLeft = linkRect.left - containerRect.left + scroller.scrollLeft;
    const linkRight = linkLeft + linkRect.width;
    const containerWidth = containerRect.width;
    const scrollLeft = scroller.scrollLeft;

    // If link is outside viewport, scroll it into view
    if (linkLeft < scrollLeft) {
      scroller.scrollTo({
        left: linkLeft - 20, // Add some padding
        behavior: 'smooth'
      });
    } else if (linkRight > scrollLeft + containerWidth) {
      scroller.scrollTo({
        left: linkRight - containerWidth + 20, // Add some padding
        behavior: 'smooth'
      });
    }
  }

  /**
   * Update section width class on section wrapper
   */
  updateSectionWidth() {
    const sectionWidthMobile = this.dataset.sectionWidthMobile || 'full-width';
    const sectionWidthDesktop = this.dataset.sectionWidthDesktop || 'full-width';
    const sectionWrapper = this.closest('.shopify-section.anchors-section');
    const isDesktop = window.innerWidth >= 750;
    
    if (sectionWrapper instanceof HTMLElement) {
      // Remove existing width classes from section wrapper
      sectionWrapper.classList.remove(
        'anchors-section--fit-width-mobile',
        'anchors-section--full-width-mobile',
        'anchors-section--fit-width-desktop',
        'anchors-section--full-width-desktop'
      );
      
      const currentWidth = isDesktop ? sectionWidthDesktop : sectionWidthMobile;
      
      if (currentWidth === 'fit-width') {
        // Add fit-width class to wrapper (mobile or desktop)
        const widthClass = isDesktop ? 'anchors-section--fit-width-desktop' : 'anchors-section--fit-width-mobile';
        sectionWrapper.classList.add(widthClass);
        
        // Find slideshow-slides element to measure its width
        const slideshowSlides = this.querySelector('slideshow-slides');
        
        if (slideshowSlides) {
          // Calculate and set width as CSS variable based on slideshow-slides
          const updateWidth = () => {
            // Use scrollWidth to get the full content width, or clientWidth if scrollWidth is smaller
            const width = Math.max(slideshowSlides.scrollWidth, slideshowSlides.clientWidth);
            // Apply width variable to the section wrapper (anchors-section)
            const widthVar = isDesktop ? '--anchors-section-width-desktop' : '--anchors-section-width-mobile';
            sectionWrapper.style.setProperty(widthVar, `${width}px`);
          };
          
          // Set initial width after a small delay to ensure content is rendered
          requestAnimationFrame(() => {
            requestAnimationFrame(updateWidth);
          });
          
          // Update on resize
          if (!this.widthResizeObserver) {
            this.widthResizeObserver = new ResizeObserver(updateWidth);
            this.widthResizeObserver.observe(slideshowSlides);
          }
        }
      } else {
        // Remove width variable for full-width
        const widthVar = isDesktop ? '--anchors-section-width-desktop' : '--anchors-section-width-mobile';
        sectionWrapper.style.removeProperty(widthVar);
        const widthClass = isDesktop ? 'anchors-section--full-width-desktop' : 'anchors-section--full-width-mobile';
        sectionWrapper.classList.add(widthClass);
        
        // Only disconnect observer if both mobile and desktop are full-width
        if (sectionWidthMobile === 'full-width' && sectionWidthDesktop === 'full-width') {
          if (this.widthResizeObserver) {
            this.widthResizeObserver.disconnect();
            this.widthResizeObserver = null;
          }
        }
      }
    }
  }

  /**
   * Update border-radius CSS variables on section wrapper
   */
  updateBorderRadius() {
    const borderRadiusMobile = this.dataset.borderRadiusMobile || '0';
    const borderRadiusDesktop = this.dataset.borderRadiusDesktop || '0';
    const sectionWrapper = this.closest('.shopify-section.anchors-section');
    
    if (sectionWrapper instanceof HTMLElement) {
      // Set border-radius CSS variables for mobile and desktop
      sectionWrapper.style.setProperty('--anchors-section-border-radius-mobile', `${borderRadiusMobile}px`);
      sectionWrapper.style.setProperty('--anchors-section-border-radius-desktop', `${borderRadiusDesktop}px`);
    }
  }

  /**
   * Update sticky position class on section wrapper
   */
  updateStickyPosition() {
    const stickyPositionMobile = this.dataset.stickyPositionMobile || 'top';
    const stickyPositionDesktop = this.dataset.stickyPositionDesktop || 'top';
    const sectionWrapper = this.closest('.shopify-section.anchors-section');
    const isDesktop = window.innerWidth >= 750;
    
    if (sectionWrapper instanceof HTMLElement) {
      // Remove existing sticky position classes
      sectionWrapper.classList.remove(
        'anchors-section--sticky-top-mobile',
        'anchors-section--sticky-bottom-mobile',
        'anchors-section--sticky-top-desktop',
        'anchors-section--sticky-bottom-desktop'
      );
      
      // Add mobile sticky position class
      sectionWrapper.classList.add(`anchors-section--sticky-${stickyPositionMobile}-mobile`);
      
      // Add desktop sticky position class
      sectionWrapper.classList.add(`anchors-section--sticky-${stickyPositionDesktop}-desktop`);
      
      // If sticky bottom (mobile or desktop), set CSS variable with section height
      const needsHeight = stickyPositionMobile === 'bottom' || stickyPositionDesktop === 'bottom';
      
      if (needsHeight) {
        const updateHeight = () => {
          const height = sectionWrapper.getBoundingClientRect().height;
          sectionWrapper.style.setProperty('--anchors-section-height', `${height}px`);
        };
        
        // Set initial height
        updateHeight();
        
        // Update on resize
        if (!this.resizeObserver) {
          this.resizeObserver = new ResizeObserver(updateHeight);
          this.resizeObserver.observe(sectionWrapper);
        }
      } else {
        // Remove height variable if neither mobile nor desktop is bottom
        sectionWrapper.style.removeProperty('--anchors-section-height');
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
      }
    }
  }
}

if (!customElements.get('anchor-navigation')) {
  customElements.define('anchor-navigation', AnchorNavigationComponent);
}

// Listen for theme editor updates
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function (event) {
    const customEvent = /** @type {CustomEvent<{sectionId: string}>} */ (event);
    const anchorNav = document.querySelector(`anchor-navigation[data-section-id="${customEvent.detail.sectionId}"]`);
    if (anchorNav && anchorNav instanceof AnchorNavigationComponent) {
      anchorNav.updateStickyPosition();
      anchorNav.updateSectionWidth();
      anchorNav.updateBorderRadius();
    }
  });
}

